import React from 'react';
import { Progress, Tag, Typography } from 'antd';
import type { ExecutionDashboardProjection } from '../../routes/execution/dashboardProjection';
import type { RouteOperationalIntelligence } from '../../operationalIntelligence/types';
import type { RouteOperationalCoordination } from '../../operationalCoordination/types';
import type { RouteOperationalRecovery } from '../../operationalRecovery/types';
import type { WorkspaceComposition } from './workspaceComposition';
import { attentionFlagLabel } from './workspaceComposition';

const { Text } = Typography;

type Props = {
  dashboard: ExecutionDashboardProjection;
  composition: WorkspaceComposition;
  intelligence?: RouteOperationalIntelligence | null;
  coordination?: RouteOperationalCoordination | null;
  recovery?: RouteOperationalRecovery | null;
};

export const ExecutionStatusStrip: React.FC<Props> = ({
  dashboard,
  composition,
  intelligence,
  coordination,
  recovery
}) => {
  const { routeStatus, progress, incidents, financeReadiness, activeOperation } = dashboard;
  const { attention, eta } = composition;
  const alertStrip =
    attention.highestPriority != null ||
    (intelligence != null && intelligence.health.state !== 'HEALTHY') ||
    (coordination != null && coordination.conflicts.hasConflict) ||
    (recovery != null && recovery.adaptiveState.state === 'CRITICAL_RECOVERY');

  return (
    <div
      className={`execution-workspace__strip${alertStrip ? ' execution-workspace__strip--alert' : ''}`}
    >
      <Tag color={routeStatus.color}>{routeStatus.label}</Tag>
      {intelligence ? (
        <Tag
          color={
            intelligence.health.state === 'CRITICAL' || intelligence.health.state === 'BLOCKED'
              ? 'error'
              : intelligence.health.state === 'HEALTHY'
                ? 'success'
                : 'warning'
          }
        >
          {intelligence.health.label}
        </Tag>
      ) : null}
      {coordination?.impactSummary ? (
        <Tag color="warning">{coordination.impactSummary}</Tag>
      ) : null}
      {recovery && recovery.adaptiveState.state !== 'NORMAL' ? (
        <Tag color={recovery.adaptiveState.state === 'CRITICAL_RECOVERY' ? 'error' : 'processing'}>
          {recovery.adaptiveState.label}
        </Tag>
      ) : null}
      {progress ? (
        <>
          <Progress
            percent={progress.routePercent}
            size="small"
            style={{ width: 88, margin: 0 }}
            showInfo={false}
          />
          <Text className="execution-workspace__muted">
            {progress.stopsCompleted}/{progress.stopsTotal}
          </Text>
        </>
      ) : null}
      <Text className="execution-workspace__muted">
        ETA: {intelligence?.eta.expectedCompletionLabel ?? eta.routeEtaLabel ?? '—'}
      </Text>
      {incidents.activeCount > 0 ? (
        <Tag color="error">Инц. {incidents.activeCount}</Tag>
      ) : null}
      <Tag color={financeReadiness.blocked ? 'error' : financeReadiness.allReady ? 'success' : 'default'}>
        {financeReadiness.blocked ? 'Оплата ✕' : financeReadiness.allReady ? 'Оплата ✓' : 'Оплата …'}
      </Tag>
      {activeOperation ? (
        <Tag color="processing">
          {activeOperation.operationType === 'loading' ? 'Погрузка' : 'Выгрузка'}
        </Tag>
      ) : null}
      {attention.flags.map((f) => (
        <Tag key={f} color={f === attention.highestPriority ? 'error' : 'warning'}>
          {attentionFlagLabel(f)}
        </Tag>
      ))}
    </div>
  );
};

export default ExecutionStatusStrip;
